import {
  useAuthActions,
  useIsAuthenticated,
  useIsAuthenticationInProgress,
  useLoggedInMember
} from "@/modules/auth/states";
import ZaniaLogoLoader from "@components/page-loader";
import { useEffect, useMemo, useState } from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { AppProtectedRoutes, AppUnprotectedRoutes } from "../routes";
// import useLoadDataOnAppLoad from "@/shared/hooks/use-load-data-start";
import { authChannel } from "@/infra/auth/broadcast";
import { AuthMessageEvent } from "@/infra/auth/type";
import { getMemberDetailsWithOrganization, getSettingsApi, getTeam, refreshAuthToken, useGetTeamsListQuery } from "@/modules/auth/requests";
import { setLoginStep, useLoginActions, useLoginStep } from "@/modules/auth/states/login";
import { DiscoveredOrganization, LOGIN_STEP } from "@/modules/auth/types";
import { setTeam } from "@/modules/team/states";
import { deleteCookie, getCookie } from "@/shared/utils/cookie-helper";
import parsePromise from "@/shared/utils/parsePromise";
import { useUserStateSelector } from "@/shared/states/user";
import { useCheckForUpdates } from "@/shared/hooks/useCheckForUpdates";
import { ROUTES } from "@/shared/constants/routes";
import AppUpdateCard from "./layout/AppUpdateCard";
import { useSessionManagement } from '../hooks/use-session-management';

function App() {
  const isAuthenticated = useIsAuthenticated();
  const isAuthenticationInProgress = useIsAuthenticationInProgress();
  const { setAuth } = useAuthActions();
  const [isSessionChecked, setIsSessionChecked] = useState(false); 
  const setAIResponseType = useUserStateSelector.useSetAIResponseType();
  const setUserResponseType = useUserStateSelector.useSetUserResponseTypes();
  const loggedInMember = useLoggedInMember();
  const loginStep = useLoginStep();
  const [hasFetchedTeam, setHasFetchedTeam] = useState(false);
  const routes = useMemo(() => {
    return isAuthenticated &&
      !isAuthenticationInProgress &&
      loginStep !== LOGIN_STEP.SELECT_ORG
      ? AppProtectedRoutes
      : AppUnprotectedRoutes;
  }, [isAuthenticated, isAuthenticationInProgress, loginStep]);

  const {
    setDiscoveredOrganizations,
  } = useLoginActions();

  const router = useMemo(() => {
    return createBrowserRouter(routes);
  }, [routes]);
  const [hasFetched, setHasFetched] = useState(false);

  const updateAvailable = useCheckForUpdates();

  // Initialize session management
  useSessionManagement();

  const { data: teamsData } = useGetTeamsListQuery({
    isEnabled: !!(isAuthenticated && loggedInMember?.member_id && getCookie('intermediate_session_token'))
  });

  useEffect(() => {
    const checkSession = async () => {
      const session = getCookie('intermediate_session_token');
  
      if (session && !hasFetched) {
        setHasFetched(true); // Prevent future API calls
  
        const [error, member] = await parsePromise(getMemberDetailsWithOrganization());
  
        if (member?.member) {
          setAuth({
            isAuthenticated: true,
            isAuthenticationInProgress: false,
            loggedInMember: member?.member,
            loggedInOrganization: member?.organization,
          });
  
          const [err, settings] = await parsePromise(getSettingsApi());
          if (settings?.data?.ui_response_quality) {
            setAIResponseType(settings.data.ui_response_quality);
            setUserResponseType(settings.data.user_response_quality);
          }
        } else {
          deleteCookie('intermediate_session_token');
          authChannel.postMessage("loggedOut");
          setLoginStep(LOGIN_STEP.MAIN);
          setAuth({
            isAuthenticated: false,
            isAuthenticationInProgress: false,
            loggedInMember: null,
            loggedInOrganization: null,
          });
        }
      } else {
        setLoginStep(LOGIN_STEP.MAIN);
        setAuth({
          isAuthenticated: false,
          isAuthenticationInProgress: false,
          loggedInMember: null,
          loggedInOrganization: null,
        });
        authChannel.postMessage("loggedOut");
      }
  
      setIsSessionChecked(true);
    };
  
    if (!hasFetched) {
      void checkSession(); // Ensure it's only called once
    }
  }, [isAuthenticated]); // Keep dependencies for reactivity
  

  useEffect(() => {
    // Add worker for refresh token
    const worker = new window.Worker(new URL("/worker.js", import.meta.url));
  
    // If the user is authenticated, start the worker
    if (loggedInMember?.member_id) {
      if(!hasFetchedTeam) {
        setHasFetchedTeam(true);
      }
      worker.postMessage("start");
  
      worker.onmessage = async (event) => {
        if (event.data === "refresh") {
          const [error, data] = await parsePromise(refreshAuthToken());
          if (error) {
            console.error(error?.message);
            return;
          }
        }
      };
    } else {
      // If the user is not authenticated, terminate the worker
      worker.terminate();
      return;
    }
  
    // Cleanup function
    return () => {
      worker.terminate();
    };
  
  }, [loggedInMember]);

  useEffect(() => {
    if (teamsData?.team_members) {
      setTeam(teamsData.team_members);
    }
  }, [teamsData]);
  
  // Handle session expiry events from activity tracker
  useEffect(() => {
    const handleSessionExpired = () => {
      
      // Clear authentication state
      deleteCookie('intermediate_session_token');
      setAuth({
        isAuthenticated: false,
        isAuthenticationInProgress: false,
        loggedInMember: null,
        loggedInOrganization: null,
      });
      
      setLoginStep(LOGIN_STEP.MAIN);
      
      // Broadcast logout to all tabs
      authChannel.postMessage("loggedOut");
    };

    window.addEventListener('session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, [setAuth]);

  // broadcast events handler to sync multiple tabs
  useEffect(() => {
    const handleMessage = (event: AuthMessageEvent) => {
      if (event.data === "loggedIn") {
        setAuth({
          isAuthenticated: true,
          isAuthenticationInProgress: false
        });
        window.location.href = `/${ROUTES.AGENT}`;
      } else if (event.data === "loggedOut") {
        setLoginStep(LOGIN_STEP.MAIN);
        setAuth({
          isAuthenticated: false,
          isAuthenticationInProgress: false,
        });
      } else if (event.data === "switchedOrganization") {
        window.location.href = `/${ROUTES.AGENT}`;
      } else if (event.data === "selectOrganization") {
        const orgs = localStorage.getItem("discoveredOrgs");
        const orgList = JSON.parse(orgs || "[]");
        if (orgList?.length) {
          setDiscoveredOrganizations(orgList as DiscoveredOrganization[])
        }
        localStorage.removeItem("discoveredOrgs")

        setLoginStep(LOGIN_STEP.SELECT_ORG);
      }
      // when the app is updated, reload the page to get the latest version
      else if (event.data === "appUpdated") {
        window.location.reload();
      }
    };

    authChannel.onmessage = handleMessage;

    return () => {
      authChannel.onmessage = null; // ✅ Just remove the listener, don't close channel
    };
  }, []);

  if (isAuthenticationInProgress || !isSessionChecked) {
    return <ZaniaLogoLoader />;
  }

  return (
    <>
      {updateAvailable && loggedInMember?.member_id && location.pathname !== '/vendor-security-form/' ? <AppUpdateCard /> : null}
      <RouterProvider router={router} fallbackElement={<ZaniaLogoLoader />} />
    </>
  );
}

export default App;